import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { Observable } from 'rxjs'
import { AlertDialogComponent, AlertDialogData } from 'src/app/common/modals/alert-dialog/alert-dialog.component'
import { MenuDialogComponent, MenuDialogData } from 'src/app/common/modals/menu-dialog/menu-dialog.component'
import { AskService } from 'src/app/services/ask/ask.service'
import { GeneralService } from 'src/app/services/general/general.service'
import {
  AskPostDetailCommentOptionParams,
  AskPostDetailOptionParams,
  AskScreenName,
} from 'src/app/services/log/ask/ask-log'
import { LogService } from 'src/app/services/log/log.service'
import { ModalService } from 'src/app/services/modal/modal.service'

@Component({
  selector: 'app-ask-reaction',
  templateUrl: './ask-reaction.component.html',
  styleUrls: ['./ask-reaction.component.scss'],
})
export class AskReactionComponent implements OnInit {
  constructor(
    private modalService: ModalService,
    private askService: AskService,
    private generalService: GeneralService,
    private logService: LogService
  ) {}

  @Input() postId: number | undefined
  @Input() commentId: number | undefined
  @Input() entryPointPostId: number | undefined
  @Input() isLikeClicked: boolean = false
  @Input() likeCount: number = 0
  @Input() showComment: boolean = false
  @Input() showOption = true
  @Input() commentCount: number = 0
  @Input() isWriter: boolean = false
  @Input() type: 'post' | 'comment' | undefined
  @Input() location: AskScreenName = 'ask_post_detail'
  @Output() onClickComment = new EventEmitter<void>()
  @Output() onClickLike = new EventEmitter<boolean>()

  ngOnInit() {}

  get id(): number | undefined {
    if (this.type === 'post') {
      return this.postId
    }
    if (this.type === 'comment') {
      return this.commentId
    }
  }

  onClickLikeButton(): void {
    if (this.id === undefined) {
      return
    }
    this.onClickLike.emit(!this.isLikeClicked)
    if (this.type === 'post') {
      this.askService.likePost(this.id).subscribe(() => {
        this.isLikeClicked = !this.isLikeClicked
        this.likeCount += this.isLikeClicked ? 1 : -1
      })
    } else {
      this.askService.likeComment(this.id).subscribe(() => {
        this.isLikeClicked = !this.isLikeClicked
        this.likeCount += this.isLikeClicked ? 1 : -1
      })
    }
  }

  async onClickOption(): Promise<void> {
    if (this.id !== undefined) {
      const menuDialog = await this.modalService.openMenuDialog(MenuDialogComponent, {
        data: this.getMenuDialogData(this.id),
      })
      await menuDialog.present()
    }
  }

  private getMenuDialogData(id: number): MenuDialogData[] {
    const menu: MenuDialogData[] = [
      {
        text: '취소',
        role: 'cancel',
        action: () => {},
      },
    ]
    if (this.isWriter) {
      menu.unshift({
        text: '삭제',
        role: 'destructive',
        action: () => {
          if (this.type === 'post') {
            this.askPostDetailPostDeleteDialogViewedLogging()
          }
          if (this.type === 'comment') {
            this.askPostDetailCommentDeleteDialogViewedLogging()
          }
          this.openConfirmDialog(id, 'delete')
        },
      })
    } else {
      menu.unshift({
        text: '신고',
        action: () => {
          if (this.type === 'post') {
            this.askPostDetailPostReportDialogViewedLogging()
          }
          if (this.type === 'comment') {
            this.askPostDetailCommentReportDialogViewedLogging()
          }
          this.openConfirmDialog(id, 'report')
        },
      })
    }
    return menu
  }

  private async openConfirmDialog(id: number, type: 'delete' | 'report'): Promise<void> {
    const dialog = await this.modalService.openDialog(AlertDialogComponent, {
      data: type === 'delete' ? this.deleteDialogData(id) : this.reportDialogData(id),
    })
    await dialog.present()
  }

  private deleteDialogData(id: number): AlertDialogData {
    return {
      image: 'assets/icons/icon-trash-bin.png',
      title: `정말로 이 ${this.type === 'post' ? '게시글' : '댓글'}을 삭제할까요?`,
      description: '이 행동은 취소할 수 없습니다.',
      buttons: [
        { text: '취소', role: 'cancel' },
        { text: '삭제', role: 'confirm', action: () => this.delete(id) },
      ],
    }
  }

  private reportDialogData(id: number): AlertDialogData {
    return {
      image: 'assets/icons/icon-trash-bin.png',
      title: `이 ${this.type === 'post' ? '게시글' : '댓글'}을 신고할까요?`,
      description: `다음과 같은 경우에 신고해 주세요.<br /> -  욕설 또는 선정적인 내용이 포함된 경우<br /> -  타인을 비방하거나 공격적인 내용이 포함된 경우<br /> -  커뮤니티의 성격을 심각히 저해하는 경우`,
      buttons: [
        { text: '취소', role: 'cancel' },
        { text: '신고', role: 'confirm', action: () => this.report(id) },
      ],
    }
  }

  private delete(id: number): Observable<void> {
    if (this.type === 'post') {
      this.askPostDetailPostDeleteClickedLogging()
      return this.askService.deletePost(id)
    } else {
      this.askPostDetailCommentDeleteClickedLogging()
      return this.askService.deleteComment(id)
    }
  }

  private report(id: number): Observable<void> {
    if (this.type === 'post') {
      this.askPostDetailPostReportClickedLogging()
      return this.askService.reportPost(id)
    } else {
      this.askPostDetailCommentReportClickedLogging()
      return this.askService.reportComment(id)
    }
  }

  /** 이하 로깅용 메소드들 */
  private askPostDetailPostReportDialogViewedLogging(): void {
    if (!this.postId) {
      return
    }
    this.logService.ask.askPostDetailPostReportDialogViewed(this.askPostDetailPostLoggingParams(this.postId))
  }

  private askPostDetailPostReportClickedLogging(): void {
    if (!this.postId) {
      return
    }
    this.logService.ask.askPostDetailPostReportClicked(this.askPostDetailPostLoggingParams(this.postId))
  }

  private askPostDetailCommentReportDialogViewedLogging(): void {
    if (!this.postId || !this.commentId) {
      return
    }
    this.logService.ask.askPostDetailCommentReportDialogViewed(
      this.askPostDetailCommentLoggingParams(this.postId, this.commentId)
    )
  }
  private askPostDetailCommentReportClickedLogging(): void {
    if (!this.postId || !this.commentId) {
      return
    }
    this.logService.ask.askPostDetailCommentReportClicked(
      this.askPostDetailCommentLoggingParams(this.postId, this.commentId)
    )
  }

  private askPostDetailPostDeleteDialogViewedLogging(): void {
    if (!this.postId) {
      return
    }
    this.logService.ask.askPostDetailPostDeleteDialogViewed(this.askPostDetailPostLoggingParams(this.postId))
  }

  private askPostDetailPostDeleteClickedLogging(): void {
    if (!this.postId) {
      return
    }
    this.logService.ask.askPostDetailPostDeleteClicked(this.askPostDetailPostLoggingParams(this.postId))
  }

  private askPostDetailCommentDeleteDialogViewedLogging(): void {
    if (!this.postId || !this.commentId) {
      return
    }
    this.logService.ask.askPostDetailCommentDeleteDialogViewed(
      this.askPostDetailCommentLoggingParams(this.postId, this.commentId)
    )
  }

  private askPostDetailCommentDeleteClickedLogging(): void {
    if (!this.postId || !this.commentId) {
      return
    }
    this.logService.ask.askPostDetailCommentDeleteClicked(
      this.askPostDetailCommentLoggingParams(this.postId, this.commentId)
    )
  }

  private askPostDetailPostLoggingParams(id: number): AskPostDetailOptionParams {
    return {
      screenName: this.location,
      postId: id,
      isEntryPost: this.entryPointPostId === id,
    }
  }

  private askPostDetailCommentLoggingParams(postId: number, commentId: number): AskPostDetailCommentOptionParams {
    return {
      screenName: this.location,
      postId: postId,
      commentId: commentId,
      isEntryPost: this.entryPointPostId === postId,
    }
  }
}
